import { useEffect, useRef } from 'react';
import {
  createParticle, densityFor, stepParticles, bonds, cursorBonds,
} from '../lib/particles.js';

/**
 * The molecular field behind the site.
 *
 * Nodes drift, near ones bond, and the cursor pulls on whatever it passes — an
 * echo of the mark in the logo rather than a generic starfield. It is fixed
 * behind every page, so the identity is the atmosphere.
 *
 * The motion itself lives in lib/particles.js, where it can be tested without a
 * browser. What is left here is the browser's half of the job, and three things
 * that only matter on someone else's machine:
 *
 *   - **Blur on retina.** A canvas sized in CSS pixels gets upscaled, so it is
 *     sized by devicePixelRatio and scaled back down.
 *   - **Battery in a background tab.** The loop stops when the page is hidden.
 *   - **Reduced motion.** Nothing animates at all for anyone who asked for less.
 */
export default function ParticleField() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    const mouse = { x: null, y: null };
    let particles = [];
    let frame = null;
    let width = 0;
    let height = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);

      // Measure the element, do not set it. The canvas is `position: fixed;
      // inset: 0`, so CSS already sizes it to the viewport correctly. Setting
      // it from window.innerWidth instead overrides that with a value that
      // *includes the scrollbar gutter*, which makes the canvas wider than the
      // content box and gives the whole site a horizontal scrollbar.
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      if (!width || !height) return;

      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const target = densityFor(width, height);
      if (particles.length > target) particles.length = target;
      while (particles.length < target) particles.push(createParticle(width, height));
    };

    const draw = () => {
      stepParticles(particles, width, height, mouse);
      ctx.clearRect(0, 0, width, height);

      // Bonds first, so nodes sit on top of them.
      ctx.lineWidth = 0.6;
      for (const { a, b, alpha } of bonds(particles)) {
        ctx.strokeStyle = `rgba(212, 19, 23, ${alpha})`;
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      }

      ctx.lineWidth = 1;
      for (const { p, alpha } of cursorBonds(particles, mouse)) {
        ctx.strokeStyle = `rgba(212, 19, 23, ${alpha})`;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(mouse.x, mouse.y);
        ctx.stroke();
      }

      ctx.fillStyle = 'rgba(212, 19, 23, 0.5)';
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      frame = requestAnimationFrame(draw);
    };

    const start = () => { if (frame === null) frame = requestAnimationFrame(draw); };
    const stop = () => { if (frame !== null) { cancelAnimationFrame(frame); frame = null; } };

    // The canvas is fixed to the viewport, so client coordinates are already
    // in the right frame — no scroll offset needed.
    const onMove = (e) => { mouse.x = e.clientX; mouse.y = e.clientY; };
    const onLeave = () => { mouse.x = null; mouse.y = null; };
    const onVisibility = () => (document.hidden ? stop() : start());

    resize();
    start();

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseleave', onLeave);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stop();
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseleave', onLeave);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <canvas ref={ref} className="particle-field" aria-hidden="true" />;
}
